'use client';

import {
  Box,
  Button,
  CloseButton,
  Drawer,
  Flex,
  Icon,
  Image,
  Link as ChakraLink,
  Portal,
  SimpleGrid,
  Text,
  HStack,
  IconButton,
  Menu,
} from '@chakra-ui/react';
import {
  IconChevronDown,
  IconCreditCard,
  IconDeviceDesktop,
  IconDots,
  IconDownload,
  IconEdit,
  IconFileText,
  IconHome,
  IconListCheck,
  IconLogout,
  IconMessages,
  IconSettings,
  IconShoppingBag,
  IconStar,
  type TablerIcon,
} from '@tabler/icons-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import NextLink from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Fragment, ReactNode, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { useDesignTheme } from '@/components/core/providers/DesignThemeProvider';
import { DownloadsMenu } from '@/components/downloads/DownloadsMenu';
import { getCreateProjectHref } from '@/components/onboarding/hybrid-onboarding-access';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { type Project, useProject } from '@/contexts/ProjectContext';
import { usePostHog } from '@/hooks/usePostHog';
import { isAiCmoBetaEnabled } from '@/lib/attune/growth/ai-cmo-feature';
import { signOutAndClearSession } from '@/lib/auth-session';
import { app, auth } from '@/lib/firebase';
import {
  DESIGN_THEME_MODE_OPTIONS,
  DESIGN_THEME_OPTIONS,
  getDesignThemeSwatches,
} from '@/theme/design-themes';
import { DataExplorer } from './DataExplorer';
import { DataExplorerBrands } from './DataExplorerBrands';
import { SidePanelLauncher } from './SidePanelTools';
import { VoiceControl } from '@/components/voice/context';
import { AppShellHeaderSlot } from './AppShellHeader';
import { useAppRailOverlay } from './AppRailContext';
import { CollapsedRailControls } from './CollapsedRailControls';
import { getPrimarySection } from './primary-navigation';
import { useMobileThreads } from './MobileThreadsContext';
import {
  getAppNavigationSections,
  getBrandSlugFromPathname,
  getResolvedNavHref,
  WORKSPACE_HOME_HREF,
  WORKSPACE_NAVIGATION_ITEMS,
} from './navigation';
import { ProductUpdatesDrawer } from './ProductUpdatesDrawer';
import { orderProjectsByFavorites } from './project-favorites';
import { useProjectFavorites } from './useProjectFavorites';
import { useTopNavFavorites } from './useTopNavFavorites';
import { useDataExplorerBrandFavorites } from './useDataExplorerBrandFavorites';

const BRAND_PAGE_PREFIX = '/app/brands/';
const AI_SEARCH_PAGE_PREFIX = '/app/ai-search/';
const WORKSPACE_NEW_CHAT_HREF = WORKSPACE_HOME_HREF;

type NavItem = {
  label: string;
  href: string;
  icon?: ReactNode;
  description?: string;
};

type NavSection = {
  title?: string;
  items: NavItem[];
};

const APP_ICON_PROPS = {
  strokeWidth: 1.75,
  absoluteStrokeWidth: true,
} as const;

function appIcon(IconComponent: TablerIcon) {
  return <IconComponent size={16} stroke={APP_ICON_PROPS.strokeWidth} />;
}

export const NAV_SECTIONS: NavSection[] = [
  ...getAppNavigationSections().map(section => ({
    title: section.title,
    items: section.items.map(item => ({
      label: item.label,
      href: item.href,
      icon: appIcon(item.icon),
      description: item.description,
    })),
  })),
];

const TOP_NAV_DROPDOWN_CONTENT_PROPS = {
  bg: 'ui.surface.card',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'ui.border.subtle',
  borderRadius: '14px',
  boxShadow: 'none',
  backdropFilter: 'blur(22px)',
  css: { WebkitBackdropFilter: 'blur(22px)' },
  p: '1',
} as const;

const TOP_NAV_DROPDOWN_ITEM_PROPS = {
  minH: '34px',
  px: '2.5',
  py: '1.5',
  borderRadius: '10px',
  color: 'ui.text.primary',
  cursor: 'pointer',
  _hover: { bg: 'ui.surface.input' },
  _focus: { bg: 'ui.surface.input' },
} as const;

export interface AppSidebarProps {
  position?: 'sticky' | 'static' | 'relative' | 'absolute' | 'fixed';
  top?: string | number;
  marginTop?: string | number;
  initialIsAdmin?: boolean;
  authPreview?: boolean;
}

function isOnboardingWorkspaceRoute(
  pathname: string,
  searchParams: { get: (key: string) => string | null }
) {
  return (
    pathname === '/app/workspace' &&
    (searchParams.get('new_project') === 'true' ||
      searchParams.get('setup') === 'true' ||
      (searchParams.get('onboarding_chat') === 'true' &&
        !searchParams.get('session') &&
        !searchParams.get('sessionId')))
  );
}

function getOnboardingWorkspaceHref(
  searchParams: { get: (key: string) => string | null },
  resetToken?: string
) {
  const params = new URLSearchParams();
  if (!resetToken && searchParams.get('project_id') && searchParams.get('new_project') !== 'true') {
    params.set('setup', 'true');
    params.set('project_id', searchParams.get('project_id')!);
  } else {
    params.set('new_project', 'true');
  }

  const workspaceId = searchParams.get('workspace_id')?.trim();
  if (workspaceId) {
    params.set('workspace_id', workspaceId);
  }

  if (resetToken) {
    params.set('reset', resetToken);
  }

  return `/app/workspace?${params.toString()}`;
}

function useAccountMenuActions() {
  const { track } = usePostHog();
  const handleBilling = async () => {
    try {
      track('sidebar_billing_clicked', { source: 'app_sidebar' });

      const functions = getFunctions(app, 'us-central1');
      const createPortalLink = httpsCallable(
        functions,
        'ext-firestore-stripe-payments-createPortalLink'
      );

      const result = await createPortalLink({
        returnUrl: `${window.location.origin}/app/workspace`,
      });

      const data = result.data as { url: string };
      window.location.assign(data.url);
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      toast.error('Failed to open billing portal');
    }
  };

  const handleSignOut = async () => {
    try {
      track('sidebar_sign_out_clicked', { source: 'app_sidebar' });
    } catch (error) {
      console.error('Failed to track sign-out analytics:', error);
    }

    const didSignOut = await signOutAndClearSession(auth);
    if (!didSignOut) {
      toast.error('Failed to sign out');
      return;
    }

    // Finish sign-out across all client providers with a fresh document boundary.
    window.location.href = '/';
  };

  return { handleBilling, handleSignOut };
}

export function AppAccountMenu({ trigger, isAdmin, railOwned = true }: { trigger: ReactNode; isAdmin: boolean; railOwned?: boolean }) {
  const router = useRouter();
  const { handleBilling, handleSignOut } = useAccountMenuActions();
  const onOpenChange = useAppRailOverlay(railOwned);
  return (
    <AccountMenu
      trigger={trigger}
      onOpenChange={onOpenChange}
      onOpenSettings={() => router.push('/app/settings')}
      onOpenShopifyApps={() => router.push('/app/admin/shopify-apps')}
      onBilling={handleBilling}
      onSignOut={handleSignOut}
      isAdmin={isAdmin}
    />
  );
}

export function AppSidebar(props: AppSidebarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const {
    currentProject,
    projects,
    manageableWorkspaces,
    canCreatePersonalProject,
    loading: projectsLoading,
    switchProject,
  } = useProject();
  const aiCmoBetaEnabled = isAiCmoBetaEnabled(currentProject);

  // Get default brand slug from current project for Visibility highlighting
  const defaultBrandSlug = currentProject?.brand_settings?.brand_slug || null;
  const hasProject = projects.length > 0;
  const activeManageableWorkspace =
    manageableWorkspaces.find(workspace => workspace.id === currentProject?.workspace_id) || null;
  const fallbackManageableWorkspace = manageableWorkspaces[0] || null;
  const workspaceForCreate =
    activeManageableWorkspace || (!canCreatePersonalProject ? fallbackManageableWorkspace : null);
  const canManageCurrentWorkspace = Boolean(workspaceForCreate);
  const canCreateProject = canCreatePersonalProject || canManageCurrentWorkspace;
  const createProjectHref = getCreateProjectHref({
    hasProject,
    canCreatePersonalProject,
    manageableWorkspaces,
    requestedWorkspaceId: workspaceForCreate?.id || null,
  });

  const hasResolvedNoProject = !projectsLoading && !hasProject;
  const navSections: NavSection[] = NAV_SECTIONS;

  const topLevelSections = navSections;
  const mobileNavSections = hasResolvedNoProject
    ? topLevelSections.filter(section => section.title === 'Home')
    : topLevelSections;
  const isOnboardingWorkspace = isOnboardingWorkspaceRoute(pathname, searchParams);
  const onboardingWorkspaceHref = getOnboardingWorkspaceHref(searchParams);

  const isAuthPreview = props.authPreview === true;
  const desktopNavSections = topLevelSections.filter(section => section.title !== 'Home');

  const { handleBilling, handleSignOut } = useAccountMenuActions();

  return (
    <>
      <Box
        as="header"
        layerStyle="appNavBar"
        px={{ base: '3', md: '3.5' }}
        py="0"
        h={{ base: '52px', md: '54px' }}
        maxH={{ base: '52px', md: '54px' }}
        w="full"
        overflow="hidden"
        position={props.position ?? 'sticky'}
        top={props.top ?? 0}
        mt={props.marginTop ?? 0}
        zIndex={20}
        borderTopWidth="1px"
        bg="ui.surface.panel"
        css={{
          '@container app-content (max-width: 47.9975rem)': {
            height: '54px',
          },
        }}
      >
        <Flex align="center" h={{ base: 'full', lg: '54px' }} position="relative">
          {/* Left: logo */}
          <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
            <ChakraLink
              as={NextLink}
              href={isOnboardingWorkspace ? onboardingWorkspaceHref : WORKSPACE_NEW_CHAT_HREF}
              display="inline-flex"
              alignItems="center"
              _hover={{ opacity: 0.8 }}
              _focus={{ outline: 'none' }}
              transition="opacity 0.15s"
            >
              <Image
                src="/logo_mini.svg"
                alt="Attune"
                boxSize="6"
                display={{ base: 'block', sm: 'none' }}
                _dark={{ filter: 'brightness(0) invert(1)' }}
              />
              <Image
                src="/logo.svg"
                alt="Attune - Answer Engine Optimization Platform"
                display={{ base: 'none', sm: 'block' }}
                width="76px"
                height="18px"
                _dark={{ filter: 'brightness(0) invert(1)' }}
              />
            </ChakraLink>
          </Box>

          {!isAuthPreview && !isOnboardingWorkspace && getPrimarySection(pathname, searchParams) !== 'library' && <CollapsedRailControls />}
          {getPrimarySection(pathname, searchParams) === 'data' ? <Box minW={0}>
            <CollapsedDesktopNavigation
              sections={desktopNavSections}
              defaultBrandSlug={defaultBrandSlug}
              disabled={isAuthPreview || isOnboardingWorkspace || hasResolvedNoProject}
            />
          </Box> : null}

          {/* Right: account controls, plus the mobile project selector. */}
          <AppShellHeaderSlot />
          <Flex align="center" gap={{ base: '1', md: '1.5' }} flexShrink={0} ml="auto" minW={0}>
            {!isAuthPreview && <VoiceControl />}
            {!isAuthPreview && <DownloadsMenu />}
            {isAuthPreview ? (
              <>
                <Box
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  h="38px"
                  px="3"
                  borderRadius="12px"
                  bg="ui.surface.input"
                  color="ui.text.secondary"
                  fontSize="0.875rem"
                  fontWeight="600"
                >
                  Sign in
                </Box>
              </>
            ) : isOnboardingWorkspace ? null : (
              <>
                <ProductUpdatesDrawer userId={user?.uid} isAdmin={props.initialIsAdmin === true} />
                {currentProject?.id && !/^\/app\/docs\/[^/]+$/.test(pathname) ? (
                  <SidePanelLauncher />
                ) : null}
                <Box display={{ base: 'block', lg: pathname === "/app/docs" ? 'block' : 'none' }}>
                  <CompactProjectSwitcher
                    currentProject={currentProject}
                    projects={projects}
                    loading={projectsLoading}
                    canCreateProject={canCreateProject}
                    onSelectProject={projectId => {
                      if (projects.some(project => project.id === projectId)) {
                        switchProject(projectId);
                      }
                    }}
                    onCreateProject={() => router.push(createProjectHref)}
                  />
                </Box>
              </>
            )}
          </Flex>
        </Flex>
      </Box>

      {!isAuthPreview && !isOnboardingWorkspace ? (
        <MobileBottomNav
          sections={mobileNavSections}
          defaultBrandSlug={defaultBrandSlug}
          aiCmoEnabled={aiCmoBetaEnabled}
          onOpenSettings={() => router.push('/app/settings')}
          onOpenShopifyApps={() => router.push('/app/admin/shopify-apps')}
          onBilling={handleBilling}
          onSignOut={handleSignOut}
          isAdmin={props.initialIsAdmin === true}
        />
      ) : null}
    </>
  );
}

export function getIsNavActive({
  pathname,
  searchParams,
  href,
  defaultBrandSlug,
  navBrandSlug,
}: {
  pathname: string;
  searchParams?: { get: (key: string) => string | null };
  href: string;
  defaultBrandSlug?: string | null;
  navBrandSlug?: string | null;
}) {
  if (href === WORKSPACE_NEW_CHAT_HREF) {
    return (
      pathname === '/app/workspace' &&
      !searchParams?.get('mode') &&
      searchParams?.get('new_project') !== 'true' &&
      searchParams?.get('setup') !== 'true' &&
      searchParams?.get('onboarding_chat') !== 'true'
    );
  }

  const [hrefPath, hrefQuery = ''] = href.split('?');
  if (hrefQuery) {
    if (pathname !== hrefPath) return false;
    const requiredParams = new URLSearchParams(hrefQuery);
    return Array.from(requiredParams.entries()).every(
      ([key, value]) => searchParams?.get(key) === value
    );
  }

  const aiSearchPath = pathname.startsWith(AI_SEARCH_PAGE_PREFIX)
    ? pathname.slice(AI_SEARCH_PAGE_PREFIX.length)
    : null;
  const isAiSearchCompetitorPath =
    aiSearchPath === 'competitors' ||
    aiSearchPath?.startsWith('competitors/') ||
    aiSearchPath === 'resolve-default';
  const aiSearchBrandPath = aiSearchPath && !isAiSearchCompetitorPath ? aiSearchPath : null;
  const legacyBrandPath = pathname.startsWith(BRAND_PAGE_PREFIX)
    ? pathname.slice(BRAND_PAGE_PREFIX.length)
    : null;
  const currentBrandPath = aiSearchBrandPath || legacyBrandPath;
  const currentBrandSlug = currentBrandPath
    ? decodeURIComponent(currentBrandPath.split('/')[0])
    : null;
  const currentBrandSubPath = currentBrandPath
    ? currentBrandPath.split('/').slice(1).filter(Boolean).join('/')
    : null;

  const activeBrandSlug = navBrandSlug ?? defaultBrandSlug;
  const isViewingActiveBrand = !!(
    activeBrandSlug &&
    currentBrandSlug &&
    currentBrandSlug.toLowerCase() === activeBrandSlug.toLowerCase()
  );

  if (href === '/app/ai-search') {
    const isAiSearchBrandPage = Boolean(
      aiSearchBrandPath &&
        currentBrandSlug &&
        (!currentBrandSubPath ||
          currentBrandSubPath === 'rank' ||
          currentBrandSubPath === 'rank-tables' ||
          currentBrandSubPath === 'sentiment')
    );
    return (
      pathname === href ||
      pathname === '/app/ai-search/competitors' ||
      pathname === '/app/ai-search/competitors/charts' ||
      pathname === '/app/visibility' ||
      pathname === '/app/rank' ||
      pathname === '/app/rank-tables' ||
      pathname === '/app/sentiment' ||
      isAiSearchBrandPage
    );
  }

  if (href === '/app/pages') {
    return pathname === href;
  }

  if (href === '/app/agents') {
    return pathname === href || pathname.startsWith('/app/agents/');
  }

  if (href === '/app/workspace') {
    return pathname === href && !searchParams?.get('mode');
  }

  if (href === '/app/ai-search/competitors') {
    return pathname === href;
  }

  if (href === '/app/ai-search/competitors/charts') {
    return pathname === href;
  }

  if (href === '/app/visibility') {
    return pathname === href || (isViewingActiveBrand && !currentBrandSubPath);
  }

  if (href === '/app/rank') {
    return pathname === href || !!(isViewingActiveBrand && currentBrandSubPath === 'rank');
  }

  if (href === '/app/rank-tables') {
    return pathname === href || !!(isViewingActiveBrand && currentBrandSubPath === 'rank-tables');
  }

  if (href === '/app/sentiment') {
    const isOnActiveBrandSentiment =
      isViewingActiveBrand &&
      currentBrandSlug &&
      pathname.startsWith(`${AI_SEARCH_PAGE_PREFIX}${currentBrandSlug}/sentiment`);
    return pathname === href || !!isOnActiveBrandSentiment;
  }

  if (href === '/app/weekly-report') {
    const isOnActiveBrandWeeklyReport =
      currentBrandSlug &&
      pathname.startsWith(`${BRAND_PAGE_PREFIX}${currentBrandSlug}/weekly-report`);
    return pathname === href || !!isOnActiveBrandWeeklyReport;
  }

  if (href === '/app/citation-report' || href === '/app/publishers') {
    return pathname === href;
  }

  if (href === '/app/citation-report/charts') {
    return pathname === href;
  }

  if (href === '/app/fan-outs') {
    const isOnActiveBrandFanOuts =
      isViewingActiveBrand &&
      currentBrandSlug &&
      pathname.startsWith(`${BRAND_PAGE_PREFIX}${currentBrandSlug}/fan-outs`);
    return pathname === href || !!isOnActiveBrandFanOuts;
  }

  return pathname === href || pathname.startsWith(href);
}

export function CollapsedDesktopNavigation({
  sections,
  defaultBrandSlug,
  disabled,
}: {
  sections: NavSection[];
  defaultBrandSlug?: string | null;
  disabled: boolean;
}) {
  const { currentProject } = useProject();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const navBrandSlug = getBrandSlugFromPathname(pathname) ?? defaultBrandSlug;
  const items = useMemo(
    () =>
      sections.flatMap(section =>
        section.items.map(item => ({ ...item, section: section.title ?? 'Other' }))
      ),
    [sections]
  );
  const { favoriteHrefs, toggleFavorite } = useTopNavFavorites(items.map(item => item.href));
  const {
    favorites: brandFavorites,
    favoritesReady,
    toggleFavorite: toggleBrandFavorite,
  } = useDataExplorerBrandFavorites(currentProject?.id);
  const favoriteItems = useMemo(
    () =>
      brandFavorites.map(brand => ({
        href: brand.href,
        label: brand.name,
        description:
          brand.description || 'Explore this brand’s visibility, rankings, and sentiment.',
        subtitle: brand.domain,
        metrics: ['Visibility', 'Rankings', 'Sentiment'],
        searchTerms: brand.aliases,
        section: 'Brands',
      })),
    [brandFavorites]
  );
  const activeItem = items
    .filter(item =>
      getIsNavActive({ pathname, searchParams, href: item.href, defaultBrandSlug, navBrandSlug })
    )
    .sort((a, b) => b.href.length - a.href.length)[0];

  return (
    <Flex
      as="nav"
      aria-label="Product navigation"
      align="center"
      flex={{ base: '0 0 auto', lg: '1' }}
      minW={0}
      ml={{ base: '2', lg: '0' }}
      mr={{ base: '1', lg: '0' }}
    >
      <DataExplorer
        items={items}
        favoriteHrefs={[...favoriteHrefs, ...brandFavorites.map(brand => brand.href)]}
        favoriteItems={favoriteItems}
        favoritesReady={favoritesReady}
        onToggleFavorite={href => {
          const brand = brandFavorites.find(brand => brand.href === href);
          if (brand) toggleBrandFavorite(brand);
          else toggleFavorite(href);
        }}
        resolveHref={href => getResolvedNavHref({ href, brandSlug: navBrandSlug, searchParams, pathname })}
        activeHref={activeItem?.href}
        disabled={disabled}
        onNavigate={href => router.push(href)}
        renderBrands={props => (
          <DataExplorerBrands
            {...props}
            favorites={brandFavorites}
            favoritesReady={favoritesReady}
            onToggleFavorite={toggleBrandFavorite}
          />
        )}
      />
    </Flex>
  );
}

function MobileBottomNav({
  sections,
  defaultBrandSlug,
  aiCmoEnabled,
  onOpenSettings,
  onOpenShopifyApps,
  onBilling,
  onSignOut,
  isAdmin,
}: {
  sections: NavSection[];
  defaultBrandSlug?: string | null;
  aiCmoEnabled: boolean;
  onOpenSettings: () => void;
  onOpenShopifyApps: () => void;
  onBilling: () => Promise<void>;
  onSignOut: () => Promise<void>;
  isAdmin: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { open: threadsOpen, openThreads } = useMobileThreads();
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const navBrandSlug = getBrandSlugFromPathname(pathname) ?? defaultBrandSlug;
  const moreProductItems = sections
    .flatMap(section => section.items)
    .filter(item => item.href !== WORKSPACE_HOME_HREF);
  const currentWorkspaceMode = searchParams.get('mode');
  const newTaskActive =
    pathname === '/app/workspace' &&
    !currentWorkspaceMode &&
    !searchParams.get('session') &&
    !searchParams.get('sessionId');
  const activityFeedActive = pathname === '/app/workspace' && currentWorkspaceMode === 'tasks';
  const docsActive = pathname === '/app/docs' || pathname.startsWith('/app/docs/');
  const isMoreActive =
    pathname === '/app/agents' ||
    pathname.startsWith('/app/agents/') ||
    pathname === '/app/settings' ||
    pathname.startsWith('/app/admin/') ||
    pathname === '/app/north-star' ||
    pathname === '/app/skills' ||
    pathname === '/app/plugins' ||
    pathname === '/app/mcp' ||
    pathname === '/app/custom-mcp' ||
    (pathname === '/app/workspace' &&
      (currentWorkspaceMode === 'agents' ||
        currentWorkspaceMode === 'growth-program' ||
        currentWorkspaceMode === 'integrations')) ||
    moreProductItems.some(item =>
      getIsNavActive({ pathname, searchParams, href: item.href, defaultBrandSlug, navBrandSlug })
    );

  return (
    <>
      <Box
        as="nav"
        aria-label="Primary mobile navigation"
        display={{ base: 'block', lg: 'none' }}
        layerStyle="appNavBar"
        position="fixed"
        left={{ base: '8px', sm: '12px', md: '16px' }}
        right={{ base: '8px', sm: '12px', md: '16px' }}
        bottom={{
          base: 'calc(env(safe-area-inset-bottom) + 6px)',
          md: 'calc(env(safe-area-inset-bottom) + 8px)',
        }}
        zIndex="overlay"
        px="1.5"
        py="1.5"
        boxShadow="none"
      >
        <Flex align="center" justify="space-between" gap="1">
          <MobileBottomNavLink
            href={WORKSPACE_HOME_HREF}
            label="New chat"
            icon={appIcon(IconEdit)}
            isActive={newTaskActive}
          />

          <Box
            as="button"
            flex="1"
            minW={0}
            h={{ base: '48px', md: '52px' }}
            borderRadius="glassButton"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="1"
            color={threadsOpen ? 'ui.text.primary' : 'ui.text.secondary'}
            bg={threadsOpen ? 'ui.surface.selected' : 'transparent'}
            borderWidth="1px"
            borderStyle="solid"
            borderColor={threadsOpen ? 'ui.border.subtle' : 'transparent'}
            cursor="pointer"
            onClick={openThreads}
            aria-label="Open threads"
            title="Threads"
            aria-expanded={threadsOpen}
            _hover={{ bg: 'ui.surface.input', color: 'ui.text.primary' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            transition="background-color 0.15s, color 0.15s, border-color 0.15s"
          >
            <Icon boxSize="5" color={threadsOpen ? 'accent.fg' : 'ui.text.tertiary'}>
              {appIcon(IconMessages)}
            </Icon>
            <Text
              as="span"
              display="none"
              fontSize="0.68rem"
              lineHeight="1"
              fontWeight={threadsOpen ? '700' : '600'}
              maxW="full"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              Threads
            </Text>
          </Box>

          <MobileBottomNavLink
            href="/app/workspace?mode=tasks&tab=tasks"
            label="Tasks"
            icon={appIcon(IconListCheck)}
            isActive={activityFeedActive}
          />

          <MobileBottomNavLink
            href="/app/docs"
            label="Docs & Assets"
            icon={appIcon(IconFileText)}
            isActive={docsActive}
          />

          <Box
            as="button"
            flex="1"
            minW={0}
            h={{ base: '48px', md: '52px' }}
            borderRadius="glassButton"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="1"
            color={isMoreActive || isMoreOpen ? 'ui.text.primary' : 'ui.text.secondary'}
            bg={isMoreActive || isMoreOpen ? 'ui.surface.selected' : 'transparent'}
            borderWidth="1px"
            borderStyle="solid"
            borderColor={isMoreActive || isMoreOpen ? 'ui.border.subtle' : 'transparent'}
            cursor="pointer"
            onClick={() => setIsMoreOpen(true)}
            aria-label="Open more navigation"
            title="More"
            aria-expanded={isMoreOpen}
            _hover={{ bg: 'ui.surface.input', color: 'ui.text.primary' }}
            _focusVisible={{ outline: 'none', boxShadow: 'none' }}
            transition="background-color 0.15s, color 0.15s, border-color 0.15s"
          >
            <Icon boxSize="5" color={isMoreActive || isMoreOpen ? 'accent.fg' : 'ui.text.tertiary'}>
              {appIcon(IconDots)}
            </Icon>
            <Text
              as="span"
              display="none"
              fontSize="0.68rem"
              lineHeight="1"
              fontWeight={isMoreActive || isMoreOpen ? '700' : '600'}
              maxW="full"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              More
            </Text>
          </Box>
        </Flex>
      </Box>

      <MobileMoreDrawer
        open={isMoreOpen}
        onOpenChange={setIsMoreOpen}
        sections={sections}
        defaultBrandSlug={defaultBrandSlug}
        aiCmoEnabled={aiCmoEnabled}
        onOpenSettings={onOpenSettings}
        onOpenShopifyApps={onOpenShopifyApps}
        onBilling={onBilling}
        onSignOut={onSignOut}
        isAdmin={isAdmin}
      />
    </>
  );
}

function MobileBottomNavLink({
  href,
  label,
  icon,
  isActive,
}: {
  href: string;
  label: string;
  icon?: ReactNode;
  isActive: boolean;
}) {
  return (
    <ChakraLink
      as={NextLink}
      href={href}
      aria-label={label}
      title={label}
      flex="1"
      minW={0}
      h={{ base: '48px', md: '52px' }}
      borderRadius="glassButton"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="1"
      color={isActive ? 'ui.text.primary' : 'ui.text.secondary'}
      bg={isActive ? 'ui.surface.selected' : 'transparent'}
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isActive ? 'ui.border.subtle' : 'transparent'}
      textDecoration="none"
      aria-current={isActive ? 'page' : undefined}
      _hover={{ bg: 'ui.surface.input', color: 'ui.text.primary', textDecoration: 'none' }}
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      transition="background-color 0.15s, color 0.15s, border-color 0.15s"
    >
      {icon ? (
        <Icon boxSize="5" color={isActive ? 'accent.fg' : 'ui.text.tertiary'} aria-hidden="true">
          {icon}
        </Icon>
      ) : null}
      <Text
        as="span"
        display="none"
        fontSize="0.68rem"
        lineHeight="1"
        fontWeight={isActive ? '700' : '600'}
        maxW="full"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </ChakraLink>
  );
}

function MobileMoreDrawer({
  open,
  onOpenChange,
  sections,
  defaultBrandSlug,
  aiCmoEnabled,
  onOpenSettings,
  onOpenShopifyApps,
  onBilling,
  onSignOut,
  isAdmin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sections: NavSection[];
  defaultBrandSlug?: string | null;
  aiCmoEnabled: boolean;
  onOpenSettings: () => void;
  onOpenShopifyApps: () => void;
  onBilling: () => Promise<void>;
  onSignOut: () => Promise<void>;
  isAdmin: boolean;
}) {
  return (
    <Drawer.Root open={open} onOpenChange={event => onOpenChange(event.open)} placement="bottom">
      <Portal>
        <Drawer.Backdrop bg="ui.overlay.backdrop" backdropFilter="blur(4px)" />
        <Drawer.Positioner>
          <Drawer.Content
            layerStyle="drawerSurface"
            borderTopRadius="0"
            borderBottomRadius="0"
            maxH="82dvh"
            overflow="hidden"
          >
            <Drawer.Header layerStyle="drawerHeader" px="4" py="3" position="relative">
              <Drawer.Title fontSize="md" fontWeight="700" color="ui.text.primary">
                Navigation
              </Drawer.Title>
              <Drawer.Description srOnly>
                Browse product areas and manage your account.
              </Drawer.Description>
              <Drawer.CloseTrigger asChild position="absolute" top="2.5" right="3">
                <CloseButton size="sm" />
              </Drawer.CloseTrigger>
            </Drawer.Header>

            <Drawer.Body
              bg="ui.surface.drawer"
              px="4"
              pt="3"
              pb="calc(env(safe-area-inset-bottom) + 18px)"
              overflowY="auto"
            >
              <Flex direction="column" gap="4">
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="ui.text.tertiary"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    mb="2"
                  >
                    Workspace
                  </Text>
                  <Flex as="ul" direction="column" gap="1" listStyleType="none" m="0" p="0">
                    {WORKSPACE_NAVIGATION_ITEMS.filter(
                      item =>
                        item.href !== WORKSPACE_HOME_HREF &&
                        item.href !== '/app/workspace?mode=tasks' &&
                        item.href !== '/app/docs' &&
                        item.href !== '/app/settings' &&
                        (item.href !== '/app/workspace?mode=growth-program' || aiCmoEnabled)
                    ).map(item => (
                      <Box as="li" key={item.href}>
                        <MobileDrawerNavLink
                          href={item.href}
                          label={item.label}
                          icon={appIcon(item.icon)}
                          defaultBrandSlug={defaultBrandSlug}
                          onNavigate={() => onOpenChange(false)}
                        />
                      </Box>
                    ))}
                  </Flex>
                </Box>
                {sections.map(section => (
                  <Box key={section.title ?? section.items[0]?.href}>
                    {section.title ? (
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        color="ui.text.tertiary"
                        textTransform="uppercase"
                        letterSpacing="0.06em"
                        mb="2"
                      >
                        {section.title}
                      </Text>
                    ) : null}
                    <Flex as="ul" direction="column" gap="1" listStyleType="none" m="0" p="0">
                      {section.items.map(item => (
                        <Box as="li" key={item.href}>
                          <MobileDrawerNavLink
                            href={item.href}
                            label={item.label}
                            icon={item.icon}
                            defaultBrandSlug={defaultBrandSlug}
                            onNavigate={() => onOpenChange(false)}
                          />
                        </Box>
                      ))}
                    </Flex>
                  </Box>
                ))}

                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color="ui.text.tertiary"
                    textTransform="uppercase"
                    letterSpacing="0.06em"
                    mb="2"
                  >
                    Account
                  </Text>
                  <Flex direction="column" gap="1">
                    {isAdmin ? (
                      <MobileDrawerActionButton
                        label="Shopify apps"
                        icon={appIcon(IconShoppingBag)}
                        onClick={() => {
                          onOpenChange(false);
                          onOpenShopifyApps();
                        }}
                      />
                    ) : null}
                    <MobileDrawerActionButton
                      label="Settings"
                      icon={appIcon(IconSettings)}
                      onClick={() => {
                        onOpenChange(false);
                        onOpenSettings();
                      }}
                    />
                    <MobileDrawerActionButton
                      label="Billing"
                      icon={appIcon(IconCreditCard)}
                      onClick={() => {
                        onOpenChange(false);
                        void onBilling();
                      }}
                    />
                    <MobileDrawerActionButton
                      label="Log Out"
                      icon={appIcon(IconLogout)}
                      onClick={() => {
                        onOpenChange(false);
                        void onSignOut();
                      }}
                    />
                  </Flex>
                </Box>
              </Flex>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}

function MobileDrawerNavLink({
  href,
  label,
  icon,
  defaultBrandSlug,
  onNavigate,
}: {
  href: string;
  label: string;
  icon?: ReactNode;
  defaultBrandSlug?: string | null;
  onNavigate: () => void;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const navBrandSlug = getBrandSlugFromPathname(pathname) ?? defaultBrandSlug;
  const isActive = getIsNavActive({ pathname, searchParams, href, defaultBrandSlug, navBrandSlug });
  const resolvedHref = getResolvedNavHref({ href, brandSlug: navBrandSlug, searchParams, pathname });

  return (
    <ChakraLink
      as={NextLink}
      href={resolvedHref}
      display="flex"
      alignItems="center"
      gap="3"
      minH="44px"
      px="3"
      borderRadius="0"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isActive ? 'accent.emphasized' : 'transparent'}
      bg={isActive ? 'accent.subtle' : 'transparent'}
      color={isActive ? 'accent.fg' : 'ui.text.primary'}
      textDecoration="none"
      aria-current={isActive ? 'page' : undefined}
      onClick={onNavigate}
      _hover={{
        bg: isActive ? 'accent.subtle' : 'ui.surface.selected',
        borderColor: isActive ? 'accent.emphasized' : 'ui.border.subtle',
        textDecoration: 'none',
      }}
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      transition="background-color 0.15s, border-color 0.15s, color 0.15s"
    >
      {icon ? (
        <Icon boxSize="4" color={isActive ? 'accent.fg' : 'ui.text.tertiary'} aria-hidden="true">
          {icon}
        </Icon>
      ) : null}
      <Text as="span" fontSize="sm" fontWeight={isActive ? '700' : '600'}>
        {label}
      </Text>
    </ChakraLink>
  );
}

function MobileDrawerActionButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      display="flex"
      alignItems="center"
      gap="3"
      minH="44px"
      px="3"
      borderRadius="0"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="transparent"
      bg="transparent"
      color="ui.text.primary"
      cursor="pointer"
      textAlign="left"
      _hover={{ bg: 'ui.surface.selected', borderColor: 'ui.border.subtle' }}
      _focusVisible={{ outline: 'none', boxShadow: 'none' }}
      transition="background-color 0.15s, border-color 0.15s"
      onClick={onClick}
    >
      <Icon boxSize="4" color="ui.text.tertiary" aria-hidden="true">
        {icon}
      </Icon>
      <Text as="span" fontSize="sm" fontWeight="600">
        {label}
      </Text>
    </Box>
  );
}

function AccountMenu({
  trigger,
  onOpenChange,
  onOpenSettings,
  onOpenShopifyApps,
  onBilling,
  onSignOut,
  isAdmin,
}: {
  trigger: ReactNode;
  onOpenChange: (details: { open: boolean }) => void;
  onOpenSettings: () => void;
  onOpenShopifyApps: () => void;
  onBilling: () => Promise<void>;
  onSignOut: () => Promise<void>;
  isAdmin: boolean;
}) {
  const { preference: designThemePreference, resolvedMode, setMode, setThemeId } = useDesignTheme();
  const [downloadingDesktop, setDownloadingDesktop] = useState(false);
  const downloadDesktop = async () => {
    if (downloadingDesktop) return;
    setDownloadingDesktop(true);
    const toastId = toast.loading('Preparing your Mac download…');
    try {
      const response = await fetch('/api/desktop/download', {
        credentials: 'include',
        cache: 'no-store',
      });
      const payload = await response.json();
      if (!response.ok || typeof payload.downloadUrl !== 'string') {
        const message =
          response.status === 401
            ? 'Please sign in again to download Attune.'
            : 'The Mac download is unavailable. Please try again later.';
        throw new Error(message);
      }
      window.location.assign(payload.downloadUrl);
      toast.success('Your Mac download is starting.', { id: toastId });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not start the Mac download.', {
        id: toastId,
      });
    } finally {
      setDownloadingDesktop(false);
    }
  };
  const selectedThemeLabel = DESIGN_THEME_OPTIONS.find(
    option => option.id === designThemePreference.themeId
  )?.label;

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      onOpenChange={onOpenChange}
      positioning={{ placement: 'top-start', strategy: 'fixed', gutter: 8 }}
    >
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>

      <Portal>
        <Menu.Positioner zIndex="dropdown">
          <Menu.Content {...TOP_NAV_DROPDOWN_CONTENT_PROPS} minW="260px" p="1.5">
            <Box px="2" py="1.5">
              <Text
                id="account-theme-label"
                fontSize="xs"
                fontWeight="700"
                color="ui.text.tertiary"
                letterSpacing="0.06em"
                textTransform="uppercase"
                mb="2"
              >
                Theme
              </Text>
              <Text fontSize="xs" color="ui.text.secondary" mb="2">
                {selectedThemeLabel ?? 'Theme'} · {resolvedMode}
              </Text>
              <SimpleGrid
                columns={4}
                gap={1}
                role="group"
                aria-labelledby="account-theme-label"
                w="full"
              >
                {DESIGN_THEME_OPTIONS.map(option => {
                  const isSelected = designThemePreference.themeId === option.id;

                  return (
                    <Box
                      key={option.id}
                      as="button"
                      onClick={() => setThemeId(option.id)}
                      aria-label={`Use ${option.label} theme`}
                      aria-pressed={isSelected}
                      title={option.label}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap="1"
                      h="30px"
                      w="full"
                      minW={0}
                      px="1.5"
                      borderRadius="glassPill"
                      borderWidth="1px"
                      borderStyle="solid"
                      borderColor={isSelected ? 'ui.border.accent' : 'ui.border.subtle'}
                      bg={isSelected ? 'accent.chip' : 'transparent'}
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{
                        bg: isSelected ? 'accent.chip' : 'ui.surface.input',
                        borderColor: isSelected ? 'ui.border.accent' : 'ui.border.default',
                      }}
                      _focusVisible={{ boxShadow: 'none' }}
                    >
                      {getDesignThemeSwatches(option.id, resolvedMode)
                        .slice(0, 3)
                        .map((color, index) => (
                          <Box
                            key={`${option.id}-${index}`}
                            boxSize="3"
                            flexShrink={0}
                            borderRadius="full"
                            bg={color}
                            borderWidth="1px"
                            borderStyle="solid"
                            borderColor="ui.border.subtle"
                          />
                        ))}
                    </Box>
                  );
                })}
              </SimpleGrid>

              <HStack
                gap={1}
                mt="2"
                flexWrap="wrap"
                role="group"
                aria-label={`Theme mode, currently ${resolvedMode}`}
              >
                {DESIGN_THEME_MODE_OPTIONS.map(option => {
                  const isSelected = designThemePreference.mode === option.value;

                  return (
                    <Box
                      key={option.value}
                      as="button"
                      onClick={() => setMode(option.value)}
                      aria-pressed={isSelected}
                      h="26px"
                      px="2.5"
                      borderRadius="glassPill"
                      borderWidth="1px"
                      borderStyle="solid"
                      borderColor={isSelected ? 'ui.border.accent' : 'ui.border.subtle'}
                      bg={isSelected ? 'accent.chip' : 'transparent'}
                      color={isSelected ? 'ui.text.accent' : 'ui.text.secondary'}
                      fontSize="xs"
                      fontWeight="600"
                      cursor="pointer"
                      transition="all 0.15s"
                      _hover={{
                        bg: isSelected ? 'accent.chip' : 'ui.surface.input',
                        color: 'ui.text.primary',
                      }}
                      _focusVisible={{ boxShadow: 'none' }}
                    >
                      {option.label}
                    </Box>
                  );
                })}
              </HStack>
            </Box>

            <Menu.Separator my="1" borderColor="ui.border.subtle" />

            <Menu.Item value="settings" {...TOP_NAV_DROPDOWN_ITEM_PROPS} onSelect={onOpenSettings}>
              <Icon color="ui.text.secondary">{appIcon(IconSettings)}</Icon>
              <Menu.ItemText>Settings</Menu.ItemText>
            </Menu.Item>

            <Menu.Item
              value="download-desktop"
              {...TOP_NAV_DROPDOWN_ITEM_PROPS}
              disabled={downloadingDesktop}
              onSelect={() => void downloadDesktop()}
            >
              <Icon color="ui.text.secondary">{appIcon(IconDownload)}</Icon>
              <Menu.ItemText>
                <Text as="span" display="block">
                  Download Attune for Mac
                </Text>
                <Text as="span" display="block" fontSize="xs" color="ui.text.tertiary">
                  Apple Silicon · Beta
                </Text>
              </Menu.ItemText>
            </Menu.Item>

            <Menu.Separator my="1" borderColor="ui.border.subtle" />

            {isAdmin ? (
              <>
                <Menu.Item
                  value="admin-shopify-apps"
                  {...TOP_NAV_DROPDOWN_ITEM_PROPS}
                  onSelect={onOpenShopifyApps}
                >
                  <Icon color="ui.text.secondary">{appIcon(IconShoppingBag)}</Icon>
                  <Menu.ItemText>Shopify apps</Menu.ItemText>
                </Menu.Item>
                <Menu.Separator my="1" borderColor="ui.border.subtle" />
              </>
            ) : null}

            <Menu.Item
              value="billing"
              {...TOP_NAV_DROPDOWN_ITEM_PROPS}
              onSelect={() => {
                void onBilling();
              }}
            >
              <Icon color="ui.text.secondary">{appIcon(IconCreditCard)}</Icon>
              <Menu.ItemText>Billing</Menu.ItemText>
            </Menu.Item>

            <Menu.Item
              value="sign-out"
              {...TOP_NAV_DROPDOWN_ITEM_PROPS}
              onSelect={() => {
                void onSignOut();
              }}
            >
              <Icon color="ui.text.secondary">{appIcon(IconLogout)}</Icon>
              <Menu.ItemText>Log Out</Menu.ItemText>
            </Menu.Item>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

type ProjectSwitcherPlacement = 'topnav' | 'rail';

const PROJECT_SWITCHER_RAIL_WIDTH = '100%';
const PROJECT_SWITCHER_TOPNAV_WIDTH = { base: '36px', sm: '136px', md: '148px' } as const;

export function CompactProjectSwitcher({
  currentProject,
  projects,
  loading,
  canCreateProject,
  onSelectProject,
  onCreateProject,
  placement = 'topnav',
}: {
  currentProject: Project | null;
  projects: Project[];
  loading: boolean;
  canCreateProject: boolean;
  onSelectProject: (projectId: string) => void;
  onCreateProject: () => void;
  placement?: ProjectSwitcherPlacement;
}) {
  const isRailPlacement = placement === 'rail';
  const onOverlayOpenChange = useAppRailOverlay(isRailPlacement);
  const { favoriteProjectIds, toggleProjectFavorite } = useProjectFavorites();
  const favoriteProjectIdSet = useMemo(() => new Set(favoriteProjectIds), [favoriteProjectIds]);
  const orderedProjects = useMemo(
    () => orderProjectsByFavorites(projects, favoriteProjectIds),
    [favoriteProjectIds, projects]
  );
  const favoriteProjectCount = orderedProjects.findIndex(
    project => !favoriteProjectIdSet.has(project.id)
  );

  if (loading) {
    return (
      <Box
        layerStyle="filterPill"
        h="34px"
        w={isRailPlacement ? PROJECT_SWITCHER_RAIL_WIDTH : PROJECT_SWITCHER_TOPNAV_WIDTH}
        maxW="full"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        color="ui.text.secondary"
      >
        <InlineSpinner size="xs" />
      </Box>
    );
  }

  if (projects.length === 0 && canCreateProject) {
    return (
      <Box
        as="button"
        layerStyle="filterPill"
        display="inline-flex"
        alignItems="center"
        justifyContent={isRailPlacement ? 'center' : undefined}
        gap="1.5"
        w={isRailPlacement ? PROJECT_SWITCHER_RAIL_WIDTH : undefined}
        maxW="full"
        minH="34px"
        px="2.5"
        border="1px dashed"
        borderColor="ui.border.subtle"
        color="ui.text.secondary"
        fontSize="0.875rem"
        cursor="pointer"
        _hover={{ borderColor: 'ui.border.default', color: 'ui.text.primary' }}
        onClick={onCreateProject}
      >
        <Text display={{ base: 'none', sm: 'inline' }}>Create project</Text>
        <Text display={{ base: 'inline', sm: 'none' }}>New</Text>
      </Box>
    );
  }

  if (projects.length === 0) {
    return (
      <Box
        layerStyle="filterPill"
        display="inline-flex"
        alignItems="center"
        justifyContent={isRailPlacement ? 'center' : undefined}
        w={isRailPlacement ? PROJECT_SWITCHER_RAIL_WIDTH : undefined}
        maxW="full"
        minH="34px"
        px="2.5"
      >
        <Text fontSize="0.875rem" color="ui.text.secondary">
          No projects
        </Text>
      </Box>
    );
  }

  return (
    <Menu.Root
      id={`compact-project-switcher-${placement}`}
      lazyMount
      unmountOnExit
      onOpenChange={onOverlayOpenChange}
    >
      <Menu.Trigger asChild>
        <Box
          as="button"
          aria-label={`Switch project. Current project: ${currentProject?.name || 'none'}`}
          display="inline-flex"
          alignItems="center"
          gap="2"
          w={isRailPlacement ? PROJECT_SWITCHER_RAIL_WIDTH : PROJECT_SWITCHER_TOPNAV_WIDTH}
          maxW="full"
          h="34px"
          px="2.5"
          borderRadius="glassInput"
          borderWidth="1px"
          borderColor="transparent"
          bg="ui.surface.input"
          cursor="pointer"
          minW={0}
          overflow="hidden"
          _hover={{ bg: 'ui.surface.selected', borderColor: 'ui.border.subtle' }}
          _focus={{ outline: 'none', boxShadow: 'none' }}
          _focusVisible={{
            outline: '2px solid',
            outlineColor: 'ui.border.accent',
          }}
          transition="background-color 120ms ease, border-color 120ms ease"
        >
          {!isRailPlacement ? (
            <Icon boxSize="3.5" color="ui.text.secondary" flexShrink={0} aria-hidden="true">
              {appIcon(IconDeviceDesktop)}
            </Icon>
          ) : null}
          <Text
            display={isRailPlacement ? 'block' : { base: 'none', sm: 'block' }}
            fontSize="13px"
            fontWeight="550"
            letterSpacing="-0.01em"
            color="ui.text.primary"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            textAlign={isRailPlacement ? 'left' : undefined}
            flex="1"
            minW={0}
          >
            {currentProject?.name || 'Select project'}
          </Text>
          <Icon
            display={isRailPlacement ? 'inline-flex' : { base: 'none', sm: 'inline-flex' }}
            boxSize="3.5"
            color="ui.text.tertiary"
            flexShrink={0}
            aria-hidden="true"
          >
            {appIcon(IconChevronDown)}
          </Icon>
        </Box>
      </Menu.Trigger>

      <Portal>
        <Menu.Positioner zIndex="dropdown">
          <Menu.Content {...TOP_NAV_DROPDOWN_CONTENT_PROPS} w="232px" minW="0" p="1.5">
            <Flex align="center" justify="space-between" px="2" pt="1" pb="1.5">
              <Text
                color="ui.text.tertiary"
                fontSize="10px"
                fontWeight="700"
                lineHeight="1"
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                Projects
              </Text>
              <Text color="ui.text.tertiary" fontSize="10px" lineHeight="1">
                {projects.length}
              </Text>
            </Flex>
            {orderedProjects.map((project, index) => {
              const isCurrent = currentProject?.id === project.id;
              const isFavorite = favoriteProjectIdSet.has(project.id);

              return (
                <Fragment key={project.id}>
                  {index === favoriteProjectCount && favoriteProjectCount > 0 ? (
                    <Menu.Separator my="1" borderColor="ui.border.subtle" />
                  ) : null}
                  <Menu.Item
                    value={project.id}
                    {...TOP_NAV_DROPDOWN_ITEM_PROPS}
                    h="8"
                    minH="8"
                    px="2"
                    py="0"
                    borderRadius="glassInput"
                    bg={isCurrent ? 'ui.surface.input' : 'transparent'}
                    onSelect={() => onSelectProject(project.id)}
                  >
                    <HStack gap="2.5" minW={0} flex="1">
                      <Icon
                        boxSize="3.5"
                        color={isCurrent ? 'ui.text.primary' : 'ui.text.secondary'}
                      >
                        {appIcon(IconDeviceDesktop)}
                      </Icon>
                      <Text
                        fontSize="13px"
                        fontWeight={isCurrent ? '600' : '550'}
                        letterSpacing="-0.01em"
                        truncate
                      >
                        {project.name}
                      </Text>
                    </HStack>
                    <IconButton
                      aria-label={`${isFavorite ? 'Remove' : 'Add'} ${project.name} ${
                        isFavorite ? 'from' : 'to'
                      } favorites`}
                      aria-pressed={isFavorite}
                      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      variant="ghost"
                      size="xs"
                      minW="8"
                      w="8"
                      h="8"
                      flexShrink={0}
                      borderRadius="glassInput"
                      color={isFavorite ? 'accent.fg' : 'ui.text.tertiary'}
                      _hover={{ bg: 'ui.surface.selected', color: 'ui.text.primary' }}
                      _focusVisible={{
                        outline: '2px solid',
                        outlineColor: 'ui.border.accent',
                      }}
                      onPointerDown={event => event.stopPropagation()}
                      onPointerUp={event => event.stopPropagation()}
                      onKeyDown={event => event.stopPropagation()}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggleProjectFavorite(project.id);
                      }}
                    >
                      <IconStar
                        size={14}
                        stroke={1.8}
                        fill={isFavorite ? 'currentColor' : 'none'}
                      />
                    </IconButton>
                  </Menu.Item>
                </Fragment>
              );
            })}

            {canCreateProject ? (
              <>
                <Menu.Separator my="1" borderColor="ui.border.subtle" />

                <Menu.Item
                  value="create-project"
                  {...TOP_NAV_DROPDOWN_ITEM_PROPS}
                  h="8"
                  minH="8"
                  px="2"
                  py="0"
                  borderRadius="glassInput"
                  fontSize="13px"
                  fontWeight="550"
                  onSelect={onCreateProject}
                >
                  <Icon boxSize="3.5" color="ui.text.secondary">
                    {appIcon(IconHome)}
                  </Icon>
                  <Menu.ItemText>Create new project</Menu.ItemText>
                </Menu.Item>
              </>
            ) : null}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
